import { useCallback, useLayoutEffect, useMemo, useRef } from "react"
import { useDeleteTemplate, useSaveTemplate } from "resources/templates"
import {
  getRestoreViewIdOnMetricsSelectorClose,
  shouldClearDuplicateReturnViewId,
} from "connected-components/asset-view/helpers/duplicate-return-view"
import { useAppSelector, useAppStore } from "hooks/redux"
import {
  assetViewSlice,
  DEFAULT_ASSET_VIEW_STATE,
  ASSET_VIEW_PREFERENCE_BASE,
  selectAssetViewPanel,
} from "connected-components/dashboard/store/asset-view-slice"
import { useDashboardSliceData } from "connected-components/dashboard/hooks/use-dashboard-slice-data"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyValue = any // groupBy / datagridState / metrics are opaque shapes (matches the slice)

// Panel-scoped asset-view data + actions, read straight from RTK (safe in any consumer, no Provider).
// Pass initialView only from the wrapper; the seed is idempotent, so no-arg calls from children are safe.
export function useAssetViewData(initialView?: string) {
  const { dashboardId, panelKey: panelId, dispatch, modelId, core, trade } = useDashboardSliceData(ASSET_VIEW_PREFERENCE_BASE)
  const store = useAppStore()

  const state = useAppSelector(
    (s) => selectAssetViewPanel(s, dashboardId, panelId) ?? DEFAULT_ASSET_VIEW_STATE,
  )

  // Seed the module-config view once. Layout effect (registered before the sync's usePreferences)
  // so the seed wins even when a cache-warm fallback resolves synchronously on mount and omits view.
  const seeded = useRef(false)
  useLayoutEffect(() => {
    if (seeded.current) return
    // Wait for a view before latching, or a config arriving on a later render never seeds.
    if (initialView == null) return
    seeded.current = true
    dispatch(assetViewSlice.actions.initFromModuleConfig({ dashboardId, panelId, view: initialView }))
  }, [dashboardId, panelId, dispatch, initialView])

  const setView = useCallback((view: string) => {
    if (shouldClearDuplicateReturnViewId(view)) {
      dispatch(assetViewSlice.actions.setDuplicateReturnViewId({ dashboardId, panelId, duplicateReturnViewId: null }))
    }
    dispatch(assetViewSlice.actions.setView({ dashboardId, panelId, view }))
  }, [dispatch, dashboardId, panelId])

  const setShowMetricsSelector = useCallback((show: boolean, { restoreSourceView = true }: { restoreSourceView?: boolean } = {}) => {
    dispatch(assetViewSlice.actions.setShowMetricsSelector({ dashboardId, panelId, showMetricsSelector: show }))
    if (!show) {
      if (restoreSourceView) {
        // Read FRESH from the store, not the render closure: callers dispatch clearDuplicateState()
        // immediately before this, so a stale closure would restore an already-cleared view.
        const current = store.getState().dashboard.assetView[dashboardId]?.[panelId] ?? DEFAULT_ASSET_VIEW_STATE
        const restoreViewId = getRestoreViewIdOnMetricsSelectorClose(current.duplicateReturnViewId, current.view)
        if (restoreViewId) {
          setView(restoreViewId)
        }
      }
      dispatch(assetViewSlice.actions.clearDuplicateState({ dashboardId, panelId }))
    }
  }, [dispatch, dashboardId, panelId, setView, store])

  const updaters = useMemo(() => ({
    setView,
    setShowMetricsSelector,
    setGroupBy: (groupBy: AnyValue) => dispatch(assetViewSlice.actions.setGroupBy({ dashboardId, panelId, groupBy })),
    setShowNotHeld: (showNotHeld: boolean | null) => dispatch(assetViewSlice.actions.setShowNotHeld({ dashboardId, panelId, showNotHeld })),
    setSelectedTableLevels: (selectedTableLevels: string[]) => dispatch(assetViewSlice.actions.setSelectedTableLevels({ dashboardId, panelId, selectedTableLevels })),
    setColumnOrder: (columnOrder: string) => dispatch(assetViewSlice.actions.setColumnOrder({ dashboardId, panelId, columnOrder })),
    setDatagridState: (datagridState: AnyValue) => dispatch(assetViewSlice.actions.setDatagridState({ dashboardId, panelId, datagridState })),
    setDuplicateFromViewName: (duplicateFromViewName: string | null) => dispatch(assetViewSlice.actions.setDuplicateFromViewName({ dashboardId, panelId, duplicateFromViewName })),
    setDuplicateFromTemplateId: (duplicateFromTemplateId: string | null) => dispatch(assetViewSlice.actions.setDuplicateFromTemplateId({ dashboardId, panelId, duplicateFromTemplateId })),
    setDuplicateFromViewMetrics: (duplicateFromViewMetrics: AnyValue) => dispatch(assetViewSlice.actions.setDuplicateFromViewMetrics({ dashboardId, panelId, duplicateFromViewMetrics })),
    setDuplicateReturnViewId: (duplicateReturnViewId: string | null) => dispatch(assetViewSlice.actions.setDuplicateReturnViewId({ dashboardId, panelId, duplicateReturnViewId })),
    clearDuplicateState: () => dispatch(assetViewSlice.actions.clearDuplicateState({ dashboardId, panelId })),
  }), [dispatch, dashboardId, panelId, setView, setShowMetricsSelector])

  const deleteTemplate = useDeleteTemplate()
  const saveTemplate = useSaveTemplate()

  // setExpanded intentionally omitted — isExpanded lives on the trade slice (per-dashboard).
  return useMemo(() => ({
    ...core,
    modelId,
    ...trade,
    ...state,
    ...updaters,
    deleteTemplate,
    saveTemplate,
  }), [core, modelId, trade, state, updaters, deleteTemplate, saveTemplate])
}
